import { useEffect, useRef } from 'react';
import { RealtimeEvent, type DirectMessagePayload } from '@nbh/shared';
import { getSocket } from './useRealtime';

/**
 * Live direct messages (decision D29).
 *
 * Separate from `useRealtime` because the two answer different questions and are
 * mounted in different places: that one is about tickets and takes a ticket id, this
 * one is about the signed-in person. There is nothing to subscribe to — the gateway
 * joins each socket to its own user room at handshake, so a message arrives whatever
 * screen is open.
 *
 * The handler is held in a ref so a parent re-render does not detach and reattach the
 * listener, which would drop anything arriving in the gap.
 */
export function useDirectMessages(onMessage: (payload: DirectMessagePayload) => void): void {
  const handler = useRef(onMessage);
  handler.current = onMessage;

  useEffect(() => {
    const socket = getSocket();
    const listener = (payload: DirectMessagePayload) => handler.current(payload);

    socket.on(RealtimeEvent.DirectMessage, listener);

    return () => {
      socket.off(RealtimeEvent.DirectMessage, listener);
    };
  }, []);
}
